import React, { useState, useCallback } from 'react';
import { Tag, Tooltip, Button, Space, Spin } from 'antd';
import { RobotOutlined, InfoCircleOutlined, CaretRightOutlined, LoadingOutlined } from '@ant-design/icons';
import {
  recommendationColor,
  recommendationLabel,
  formatConfidence,
  formatCurrencyAmount,
  isLowConfidence,
} from './SaaSProAdvisoryShared';

const stopBubble = (e) => {
  if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
};

/**
 * Inline advisory chip + cost delta + rationale disclosure for a single claim
 * item row on the PA detail page.
 *
 * @param {Object} props
 * @param {import('../../assets/utils/lib').SaaSProAgentDecision|null|undefined} props.decision
 * @param {number|null|undefined} props.requestedCost — original `cost` for the line so we can show a delta.
 * @param {boolean} [props.compact] — table cell: 2-line rationale snippet, no expand control.
 * @param {boolean} [props.forceExpanded] — item-details modal: always show full rationale.
 * @param {string} [props.pendingLabel]
 */
const SaaSProAdvisoryChip = ({
  decision,
  requestedCost,
  compact = false,
  forceExpanded = false,
  pendingLabel = 'Awaiting AI review\u2026',
}) => {
  const [expanded, setExpanded] = useState(Boolean(forceExpanded));
  const toggleExpanded = useCallback((e) => {
    stopBubble(e);
    setExpanded((v) => !v);
  }, []);

  if (!decision) {
    return (
      <span
        className="saaspro-advisory saaspro-advisory--pending"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          fontStyle: 'italic',
          color: 'rgba(0,0,0,0.45)',
          fontSize: forceExpanded ? 13 : 12,
        }}
        onClick={stopBubble}
      >
        {forceExpanded ? (
          <Spin indicator={<LoadingOutlined style={{ fontSize: 14 }} spin />} size="small" />
        ) : (
          <RobotOutlined />
        )}
        {pendingLabel}
      </span>
    );
  }

  const isSuperseded = decision.status && decision.status !== 'advisory';
  const recColor = recommendationColor(decision.recommendation);
  const recLabel = recommendationLabel(decision.recommendation);
  const confidence = formatConfidence(decision.confidence);
  const lowConfidence = isLowConfidence(decision.confidence);

  const recommendedCost = decision.recommended_approved_cost;
  const requested = Number(requestedCost || 0);
  const showCostDelta = (
    recommendedCost != null
    && Number.isFinite(Number(recommendedCost))
    && Number(recommendedCost) !== requested
  );
  const costDirection = showCostDelta
    ? (Number(recommendedCost) < requested ? 'down' : 'up')
    : null;

  const citations = Array.isArray(decision.policy_citations) ? decision.policy_citations : [];
  const hasRationale = Boolean(decision.rationale);
  const rationaleSnippet = hasRationale
    ? String(decision.rationale).replace(/\s+/g, ' ').trim()
    : '';
  const rationalePreview = rationaleSnippet.length > 120
    ? `${rationaleSnippet.slice(0, 119)}\u2026`
    : rationaleSnippet;
  const showFullRationale = hasRationale && (forceExpanded || (expanded && !compact));

  return (
    <div
      className={`saaspro-advisory ${compact ? 'saaspro-advisory--compact' : ''} ${forceExpanded ? 'saaspro-advisory--detail' : ''} ${isSuperseded ? 'saaspro-advisory--superseded' : ''}`}
      onClick={stopBubble}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: forceExpanded ? 8 : 4,
        // Keep decided-row advisories readable in the table; only dim when expanded/non-compact.
        opacity: isSuperseded && !compact && !forceExpanded ? 0.55 : 1,
      }}
    >
      <Space size={6} wrap align="center">
        <Tooltip title="SaaSPro AI advisory (read-only)">
          <RobotOutlined style={{ color: 'rgba(0,0,0,0.55)' }} />
        </Tooltip>
        <Tag color={recColor} style={{ marginInlineEnd: 0, fontWeight: 600 }}>
          {isSuperseded && !compact && !forceExpanded ? `Superseded \u2022 ${recLabel}` : recLabel}
        </Tag>
        {showCostDelta && (
          <Tooltip title={`Requested: ${formatCurrencyAmount(requested)}`}>
            <span style={{ fontSize: forceExpanded ? 13 : 12, color: 'rgba(0,0,0,0.65)', fontWeight: 600 }}>
              {costDirection === 'down' ? '\u2193' : '\u2191'} {formatCurrencyAmount(recommendedCost)}
            </span>
          </Tooltip>
        )}
        {confidence && (!compact || forceExpanded) && (
          <Tooltip title={lowConfidence ? 'Low confidence \u2014 review carefully' : 'AI confidence score'}>
            <span style={{
              fontSize: 12,
              color: lowConfidence ? '#d4380d' : 'rgba(0,0,0,0.45)',
              fontWeight: lowConfidence ? 600 : 400,
            }}>
              {confidence}
              {lowConfidence ? <InfoCircleOutlined style={{ marginLeft: 4 }} /> : null}
            </span>
          </Tooltip>
        )}
        {hasRationale && !compact && !forceExpanded && (
          <Button
            type="link"
            size="small"
            onClick={toggleExpanded}
            style={{ padding: 0, height: 'auto', fontSize: 12 }}
          >
            <CaretRightOutlined rotate={expanded ? 90 : 0} />
            {' '}AI rationale
          </Button>
        )}
      </Space>

      {hasRationale && compact && !forceExpanded && (
        <Tooltip title={rationaleSnippet}>
          <div
            className="saaspro-advisory__snippet"
            style={{
              fontSize: 11,
              lineHeight: 1.35,
              color: 'rgba(0,0,0,0.55)',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {rationalePreview}
          </div>
        </Tooltip>
      )}

      {showFullRationale && (
        <div
          className="saaspro-advisory__rationale"
          style={{
            background: forceExpanded ? 'rgba(15, 118, 110, 0.04)' : 'rgba(0,0,0,0.03)',
            border: forceExpanded ? '1px solid rgba(15, 118, 110, 0.12)' : '1px solid rgba(0,0,0,0.06)',
            borderRadius: 6,
            padding: forceExpanded ? 10 : 8,
            fontSize: forceExpanded ? 13 : 12,
            lineHeight: 1.45,
            color: 'rgba(0,0,0,0.75)',
            whiteSpace: 'pre-wrap',
          }}
        >
          {decision.rationale}
          {citations.length > 0 && (
            <div style={{ marginTop: 6 }}>
              {citations.map((cite, idx) => {
                const isUrl = typeof cite === 'string' && /^https?:\/\//i.test(cite);
                return (
                  <Tag
                    key={`${cite}-${idx}`}
                    color="geekblue"
                    style={{ marginInlineEnd: 4, marginBottom: 4 }}
                  >
                    {isUrl ? (
                      <a href={cite} target="_blank" rel="noreferrer" onClick={stopBubble}>
                        {cite}
                      </a>
                    ) : cite}
                  </Tag>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SaaSProAdvisoryChip;
